import { isFunction } from 'lodash/fp';

import getWebCryptoSubtle from '../../../../../../utils/web-crypto';

export const stripPem = (pem: string) => pem.replace(/-----[^-]+-----/g, '').replace(/\s+/g, '');

export const encodeText = (text: string) => new TextEncoder().encode(text);

// Web Crypto API, btoa and atob are widely available across major browsers
// https://caniuse.com/mdn-api_crypto_subtle, https://caniuse.com/atob-btoa
// If these errors become frequent on BugSnag, we can consider server-side encryption instead.
const checkBrowserCapabilities = () => {
  getWebCryptoSubtle();
  if (!isFunction(window.atob) || !isFunction(window.btoa)) {
    throw new Error('atob or btoa function not available in this environment.');
  }
};

const base64ToArrayBuffer = (base64: string): ArrayBuffer => {
  const binaryString = window.atob(base64);
  const size = binaryString.length;
  const bytes = new Uint8Array(size);
  for (let i = 0; i < size; i += 1) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes.buffer;
};

const arrayBufferToBase64 = (buffer: ArrayBuffer): string => {
  const chars = [];
  const bytes = new Uint8Array(buffer);
  for (let i = 0; i < bytes.length; i += 1) {
    chars.push(String.fromCharCode(bytes[i]!));
  }
  return window.btoa(chars.join(''));
};

const importPublicKey = async (publicKeyPem: string): Promise<CryptoKey> => {
  const subtleCrypto = getWebCryptoSubtle();
  const keyBytes = base64ToArrayBuffer(stripPem(publicKeyPem));

  const key = await subtleCrypto.importKey(
    'spki', // SubjectPublicKeyInfo
    keyBytes,
    {
      hash: 'SHA-256',
      name: 'RSA-OAEP',
    },
    false, // not extractable
    ['encrypt'],
  );

  // CryptoKey.algorithm is typed as the very generic KeyAlgorithm, which doesn’t include RSA-specific fields, like modulusLength.
  const { modulusLength } = key.algorithm as { modulusLength?: number };
  if (modulusLength !== 2048) {
    throw new Error(`The public key must be 2048-bit (got ${modulusLength}-bit).`);
  }

  return key;
};

const encryptString = async (publicKeyPem: string, textToEncrypt: string): Promise<string> => {
  try {
    if (!textToEncrypt || textToEncrypt.length === 0) {
      throw new Error('textToEncrypt cannot be empty.');
    }

    if (!publicKeyPem || publicKeyPem.length === 0) {
      throw new Error('publicKeyPem cannot be empty.');
    }

    checkBrowserCapabilities();

    const encodedText = encodeText(textToEncrypt);

    // RSA-OAEP plaintext size limit is around 190 bytes when using a 2048-bit key with SHA-256
    const maxTextLength = 190; // (modulusLength/8) - 2*hashLength - 2 = 2048/8 - 2*32 - 2 = 190
    if (encodedText.length > maxTextLength) {
      throw new Error('textToEncrypt string is too large for RSA-OAEP encryption. Max ~190 bytes allowed.');
    }

    const publicKey = await importPublicKey(publicKeyPem);
    const subtleCrypto = getWebCryptoSubtle();

    const encryptedData = await subtleCrypto.encrypt(
      {
        name: 'RSA-OAEP',
      },
      publicKey,
      encodedText,
    );

    return arrayBufferToBase64(encryptedData);
  } catch (error) {
    throw new Error(`Encryption failed: ${(error as Error).message}`);
  }
};

export default encryptString;
